export interface CapturedVoiceInput {
  readonly mediaType: string;
  readonly dataUrl: string;
  readonly byteLength: number;
}

export interface VoiceCaptureEnvironment {
  readonly mediaDevices?: Pick<MediaDevices, "getUserMedia">;
  readonly MediaRecorderConstructor?: typeof MediaRecorder;
  readonly maxDurationMs?: number;
  readonly maxBytes?: number;
}

function blobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The voice prompt could not be prepared."));
    reader.onload = () => typeof reader.result === "string"
      ? resolve(reader.result)
      : reject(new Error("The voice prompt could not be prepared."));
    reader.readAsDataURL(blob);
  });
}

/**
 * Captures one short, user-initiated voice prompt. The caller must immediately
 * transmit the returned value and discard it; this helper never writes audio to
 * local or account storage.
 */
export async function captureTransientVoiceInput(environment: VoiceCaptureEnvironment = {}): Promise<CapturedVoiceInput> {
  const mediaDevices = environment.mediaDevices ?? globalThis.navigator?.mediaDevices;
  const Recorder = environment.MediaRecorderConstructor ?? globalThis.MediaRecorder;
  if (mediaDevices === undefined || Recorder === undefined) {
    throw new Error("Voice input is unavailable on this device. Type the prompt instead.");
  }

  const stream = await mediaDevices.getUserMedia({ audio: true, video: false });
  const chunks: BlobPart[] = [];
  const recorder = new Recorder(stream, { audioBitsPerSecond: 64_000 });
  const stopped = new Promise<void>((resolve, reject) => {
    recorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    });
    recorder.addEventListener("error", () => reject(new Error("Voice capture failed. Type the prompt instead.")), { once: true });
    recorder.addEventListener("stop", () => resolve(), { once: true });
  });

  const duration = Math.min(Math.max(environment.maxDurationMs ?? 10_000, 1_000), 30_000);
  try {
    recorder.start(250);
    await new Promise((resolve) => globalThis.setTimeout(resolve, duration));
    if (recorder.state !== "inactive") recorder.stop();
    await stopped;
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }

  const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
  if (blob.size === 0) throw new Error("No voice prompt was recorded. Try again or type the prompt.");
  const maxBytes = environment.maxBytes ?? 650_000;
  if (blob.size > maxBytes) throw new Error("The voice prompt is too large. Keep it shorter or type the prompt.");
  return Object.freeze({ mediaType: blob.type, dataUrl: await blobAsDataUrl(blob), byteLength: blob.size });
}
